import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import FinancialProductPropertiesTable from './FinancialProductPropertiesTable';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useTranslation } from 'react-i18next';
import { useMutation, useSubscription } from 'hooks/apollo';
import { REMOVE_FINANCIAL_PRODUCT_PROPERTY, ADD_UPDATE_FINANCIAL_PRODUCT_PROPERTY } from 'features/financialProducts/edit/mutations';
import { FINANCIAL_PRODUCT_PROPERTY_DELETED, FINANCIAL_PRODUCT_PROPERTY_UPDATED } from '../../../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { get, over, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { remove } from 'ramda';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';
import usePropertiesData, { PropertyContext } from 'features/common/components/property-selector/usePropertiesData';

const FinancialProductPropertiesContainer = ({ productId, properties, isNewFinancialProduct, canWrite }) => {
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [t] = useTranslation();
  const addToast = useToast();

  const {
    properties: propertiesData,
    validation: propertiesValidation,
    validate: validateProperties,
    resetValidation
  } = usePropertiesData(PropertyContext.FinancialProduct, { requiredValue: true });

  useEffect(() => {
    resetLens(
      properties?.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        elementLocal.isSaving = false;
        return elementLocal;
      }) || []
    );
  }, [resetLens, properties]);

  useEffect(() => {
    validateProperties(lens |> get, dirtyInfo);
  }, [lens, validateProperties, dirtyInfo]);

  const [saveProperty] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_PROPERTY);

  const [deleteProperty] = useMutation(REMOVE_FINANCIAL_PRODUCT_PROPERTY);

  useSubscription(FINANCIAL_PRODUCT_PROPERTY_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Properties.PropertyDeleted'), 'success');
      const lensLocal = lens |> get;
      const objTL = lensLocal.filter(
        el => el.leasingFinancialProductPropertyId === data.financialProductPropertyDeleted.leasingFinancialProductPropertyId
      );
      lensLocal.splice(lensLocal.indexOf(objTL), 1);
      resetLens(lensLocal);
      client.cache.evict({
        id: `FinancialProductItem:{"productId":${productId}}`,
        fieldName: 'financialProductProperties'
      });
      client.cache.gc();
    }
  });

  useSubscription(FINANCIAL_PRODUCT_PROPERTY_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Properties.PropertyUpdated'), 'success');
      const lensLocal = [...(lens |> get)];
      lensLocal.forEach(element => {
        if (
          (element.leasingFinancialProductPropertyId &&
            element.leasingFinancialProductPropertyId === data.financialProductPropertyUpdated.leasingFinancialProductPropertyId) ||
          (!element.leasingFinancialProductPropertyId &&
            element.productId === data.financialProductPropertyUpdated.productId &&
            element.propertyId === data.financialProductPropertyUpdated.propertyId)
        ) {
          element.isInEditing = false;
          element.isSaving = false;
        }
      });
      resetLens(lensLocal);
      client.cache.evict({
        id: `FinancialProductItem:{"productId":${productId}}`,
        fieldName: 'financialProductProperties'
      });
      client.cache.gc();
    }
  });

  const handleCancelEdit = useCallback(
    (propertyId, orderNo) => {
      if (!propertyId) resetLens(remove(orderNo, 1, lens |> get));
      else {
        const original = properties.find(x => x.propertyId === propertyId);
        over(lens[orderNo], prevValue => ({ ...prevValue, ...original, isInEditing: false }));
      }
    },
    [lens, properties, resetLens]
  );

  const handleAdd = useCallback(() => {
    resetLens([
      ...(lens |> get),
      {
        productId: productId,
        propertyId: null,
        propertyName: '',
        propertyValue: '',
        mandatory: false,
        isInEditing: true
      }
    ]);
    resetValidation();
  }, [lens, productId, resetLens, resetValidation]);

  const handleDelete = useCallback(
    (leasingFinancialProductPropertyId, orderNo) => {
      if (!leasingFinancialProductPropertyId) resetLens(remove(orderNo, 1, lens |> get));
      else if (window.confirm(t('FinancialProduct.Details.Properties.DeletePropertyConfirmation'))) {
        deleteProperty({
          variables: {
            input: { leasingFinancialProductPropertyId }
          }
        });
      }
    },
    [deleteProperty, lens, resetLens, t]
  );

  const handleSave = useCallback(
    orderNo => {
      if ((lens |> get)[orderNo].propertyValue === '' || (lens |> get)[orderNo].propertyName === '') {
        validateProperties(lens |> get);
        return;
      }
      resetLens([...(lens |> get)], {
        [orderNo]: {
          isSaving: true,
          isInEditing: false
        }
      });
      const elementTL = [...(lens |> get)][orderNo];

      if (elementTL.propertyValue instanceof Date) elementTL.propertyValue = elementTL.propertyValue.toLocaleDateString();
      else elementTL.propertyValue = elementTL.propertyValue?.toString() || null;

      saveProperty({
        variables: {
          input: {
            leasingFinancialProductPropertyId: elementTL.leasingFinancialProductPropertyId,
            productId: elementTL.productId,
            propertyId: elementTL.propertyId,
            propertyValue: elementTL.propertyValue,
            mandatory: elementTL.mandatory
          }
        }
      });
    },
    [lens, validateProperties, resetLens, saveProperty]
  );

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <FinancialProductPropertiesTable
      canWrite={canWrite}
      isNewFinancialProduct={isNewFinancialProduct}
      onAdd={handleAdd}
      onCancelEdit={handleCancelEdit}
      onDelete={handleDelete}
      onSave={handleSave}
      properties={lens}
      propertiesData={propertiesData}
      validation={propertiesValidation}
    />
  );
};

FinancialProductPropertiesContainer.propTypes = {
  productId: PropTypes.number.isRequired,
  properties: PropTypes.array.isRequired,
  isNewFinancialProduct: PropTypes.bool.isRequired,
  canWrite: PropTypes.bool.isRequired
};

export default FinancialProductPropertiesContainer;
